import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import { getCroppedImage } from '../../utils/imageUtils';
import './ImageCropper.css';

interface ImageCropperProps {
    imageSrc: string;
    onCropComplete: (croppedImage: Blob) => void;
    onCancel: () => void;
    aspectRatio?: number;
    shape?: 'rect' | 'round';
}

export default function ImageCropper({
    imageSrc,
    onCropComplete,
    onCancel,
    aspectRatio = 1,
    shape = 'round'
}: ImageCropperProps) {
    const { t } = useTranslation();
    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [processing, setProcessing] = useState(false);

    const onCropAreaChange = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleCropComplete = useCallback(async () => {
        if (!croppedAreaPixels) return;

        try {
            setProcessing(true);
            const croppedImage = await getCroppedImage(
                imageSrc,
                croppedAreaPixels,
                0 // rotation - we can add this later if needed
            );
            onCropComplete(croppedImage);
        } catch (error) {
            console.error('Failed to crop image:', error);
        } finally {
            setProcessing(false);
        }
    }, [imageSrc, croppedAreaPixels, onCropComplete]);

    return (
        <div className="image-cropper-container">
            <div className="image-cropper-modal">
                <div className="cropper-header">
                    <h3>{t('imageCropper.title')}</h3>
                    <p className="cropper-hint">{t('imageCropper.hint')}</p>
                </div>

                <div className="cropper-area">
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={aspectRatio}
                        cropShape={shape}
                        showGrid={false}
                        onCropChange={setCrop}
                        onCropComplete={onCropAreaChange}
                        onZoomChange={setZoom}
                    />
                </div>

                <div className="cropper-controls">
                    <label htmlFor="zoom-slider" className="zoom-label">
                        {t('imageCropper.zoom')}
                    </label>
                    <input
                        id="zoom-slider"
                        type="range"
                        min={1}
                        max={3}
                        step={0.1}
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                        className="zoom-slider"
                    />
                </div>

                <div className="cropper-actions">
                    <button
                        type="button"
                        className="cancel-button"
                        onClick={onCancel}
                        disabled={processing}
                    >
                        {t('imageCropper.cancel')}
                    </button>
                    <button
                        type="button"
                        className="save-button"
                        onClick={handleCropComplete}
                        disabled={processing}
                    >
                        {processing ? t('imageCropper.processing') : t('imageCropper.save')}
                    </button>
                </div>
            </div>
        </div>
    );
}
